import type { AxiosResponse } from "axios";
import { v4 } from "uuid";

import {
  pickRandom,
  SAMPLE_FORENAMES,
  SAMPLE_SURNAMES,
} from "@thalamos/common";
import type {
  FormContextMiniForm,
  SoadT2Part1DataSchema,
} from "@thalamos/aspire-common";
import { exampleSoadT2Data, exampleSRFData } from "@thalamos/aspire-common";

import { api } from "~/api.js";

const soadReportFormTemplateId = "soad-report-form";
const certificateTemplateId = "mha-soad-t2";

export type SignableSoadAssessmentResult = {
  patientId: string;
  formContextId: string;
  requestId: string;
};

/**
 * Progress reported as data rather than text, so the display strings stay with
 * the component that renders them.
 */
export type SignableSoadAssessmentStep =
  | { kind: "creatingPatient" }
  | { kind: "findingPatientInPds" }
  | { kind: "creatingAssessment"; requestId: string }
  | { kind: "completingReportSections" }
  | { kind: "addingCertificate" }
  | { kind: "completingCertificate" };

export type SignableSoadAssessmentArgs = {
  /** When omitted, a local patient is created instead of a PDS-linked one. */
  nhsNumber?: string;
  onStep: (step: SignableSoadAssessmentStep) => void;
};

/**
 * The shared axios instance resolves below 500 rather than throwing, so every
 * call in this module checks its status by hand.
 */
function failureDetail(response: AxiosResponse<unknown>): string {
  const reason =
    typeof response.data === "object" &&
    response.data !== null &&
    "reason" in response.data
      ? String(response.data.reason)
      : undefined;

  return reason ?? `HTTP ${String(response.status)}`;
}

async function resolvePdsPatientId(nhsNumber: string): Promise<string> {
  const search = await api.patients.searchV2(v4(), {
    type: "nhs-number",
    nhsNumber,
    patientSearchVariant: "normal",
  });

  if (search.status !== 200) {
    throw new Error(`Patient search failed: ${failureDetail(search)}`);
  }

  if (search.data.searchOutcome === "too-many-matches") {
    throw new Error("Patient search returned too many matches");
  }

  const match = search.data.matchedPatients[0];
  if (!match) {
    throw new Error(
      `No patient found for NHS number ${nhsNumber}. Check that PDS is reachable and that this number exists in it.`,
    );
  }

  // Saving the reasoning is what creates the patient and writes its PDS link.
  const reasoning = await api.patients.addSearchV2Reasoning(match.resultId, {
    reason: "SOAD assessment",
  });

  if (reasoning.status !== 200) {
    throw new Error(`Search reasoning failed: ${failureDetail(reasoning)}`);
  }

  if (
    reasoning.data.type !== "patient-page" &&
    reasoning.data.type !== "s-flag-setup"
  ) {
    throw new Error(
      `Patient needs manual attention before it can be used: ${reasoning.data.type}`,
    );
  }

  return reasoning.data.patientId;
}

async function createLocalPatient(): Promise<string> {
  const patientId = v4();

  const created = await api.patients.create(patientId, {
    type: "demographics",
    demographics: {
      name: {
        given: pickRandom(SAMPLE_FORENAMES),
        family: pickRandom(SAMPLE_SURNAMES),
      },
      address: {
        address: "42 Lavender Hill, London",
        postalCode: "SW11 5RW",
      },
      dateOfBirth: "1990-01-01",
    },
  });

  if (created.status !== 200) {
    throw new Error(`Failed to create patient: ${failureDetail(created)}`);
  }

  return patientId;
}

async function getForms(formContextId: string): Promise<FormContextMiniForm[]> {
  const response = await api.forms.getFormContext(formContextId);

  if (response.status !== 200 || !response.data) {
    throw new Error(`Failed to read assessment: ${failureDetail(response)}`);
  }

  return response.data.forms;
}

async function completeDraft(draftId: string, data: unknown): Promise<void> {
  const response = await api.drafts.updateData(draftId, data);

  if (response.status !== 204) {
    throw new Error(`Failed to save section: ${failureDetail(response)}`);
  }
}

/**
 * Builds a SOAD assessment populated to the point of being signable: every SRF
 * part complete plus one complete statutory certificate.
 *
 * Supplying an NHS number routes patient creation through the real PDS
 * search-and-link path rather than creating a local record, so behaviour that
 * depends on a linked patient can be exercised.
 */
export async function createSignableSoadAssessment({
  nhsNumber,
  onStep,
}: SignableSoadAssessmentArgs): Promise<SignableSoadAssessmentResult> {
  const requestId = `QC-${v4().slice(0, 6).toUpperCase()}`;
  const formContextId = v4();

  onStep(
    nhsNumber ? { kind: "findingPatientInPds" } : { kind: "creatingPatient" },
  );
  const patientId = nhsNumber
    ? await resolvePdsPatientId(nhsNumber)
    : await createLocalPatient();

  onStep({ kind: "creatingAssessment", requestId });
  const createdContext = await api.forms.createSoadAssessmentContext(
    formContextId,
    patientId,
    requestId,
  );
  if (createdContext.status !== 204) {
    throw new Error(
      `Failed to create assessment: ${failureDetail(createdContext)}`,
    );
  }

  // Creating the context also creates the report form and a draft per part.
  const reportForm = (await getForms(formContextId)).find(
    (form) => form.formTemplate.id === soadReportFormTemplateId,
  );
  if (!reportForm?.allDrafts) {
    throw new Error("Assessment was created without a report form");
  }

  onStep({ kind: "completingReportSections" });
  for (const draft of reportForm.allDrafts) {
    const samplePart = exampleSRFData[draft.part - 1];
    if (!samplePart) {
      throw new Error(
        `No sample data for report form part ${String(draft.part)}`,
      );
    }

    await completeDraft(draft.id, {
      ...samplePart.data,
      completionStatus: "completed",
    });
  }

  // Certificates must be filled after the report form: saving any report part
  // resets every certificate on the assessment back to in-progress.
  onStep({ kind: "addingCertificate" });
  const addedCertificate = await api.soad.addStatutoryCertificate(
    formContextId,
    certificateTemplateId,
  );
  if (addedCertificate.status !== 204) {
    throw new Error(
      `Failed to add certificate: ${failureDetail(addedCertificate)}`,
    );
  }

  const certificate = (await getForms(formContextId)).find(
    (form) => form.formTemplate.id === certificateTemplateId,
  );
  const certificateDraft = certificate?.allDrafts?.[0];
  if (!certificateDraft) {
    throw new Error("Certificate was created without a draft");
  }

  // Draft data is untyped in transit; the template id above fixes its shape.
  const { practitioner } = certificateDraft.data as SoadT2Part1DataSchema;

  onStep({ kind: "completingCertificate" });
  await completeDraft(certificateDraft.id, {
    ...exampleSoadT2Data[0].data,
    // Keep the server-seeded practitioner — it identifies the signed-in user,
    // which the fixture's placeholder cannot — and only confirm the address.
    practitioner: { ...practitioner, isConfirmed: true },
    completionStatus: "completed",
  });

  return { patientId, formContextId, requestId };
}
